// src/pages/teams/TeamSeasonsList.jsx
//
// Per-team Seasons listing. Mounted at /teams/:teamId/seasons and
// linked from the new "Seasons" tab in TeamSubNav.
//
// Why this page exists (May-2026):
//   The founder said "is schedule the same as season? for me schedule
//   is a by product of season." Until now, Seasons weren't a first-
//   class concept in the per-team chrome — coaches had to navigate
//   to /seasons (currentTeam-scoped) or know that the "Schedule" tab
//   was implicitly only ever showing the active season. This page
//   surfaces the actual mental model: a team owns many seasons,
//   each season owns its own schedule/registrations/roster/etc.
//
// What this page is NOT:
//   - Not a replacement for /seasons (the cross-team picker that
//     sits in the sidebar, scoped via TeamContext).
//   - Not a schedule view. The "View" CTA on each row goes to
//     SeasonDetail (/seasons/:id), which then offers Schedule as
//     one of many drilldowns. Schedule is one feature of a season,
//     not the season itself.
//   - Not an editor. Toggling status / archiving / editing a
//     season still happens on SeasonDetail.

import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  collection,
  query,
  where,
  onSnapshot,
} from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import { format } from 'date-fns';
import clsx from 'clsx';
import {
  PlusIcon,
  CalendarDaysIcon,
  UserGroupIcon,
  ClipboardDocumentListIcon,
  ArrowRightIcon,
  ArchiveBoxIcon,
  LockOpenIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';
import TeamSubNav from '@/components/team/TeamSubNav';
import { toSeasonDate } from '@/utils/seasonDates';

const HEAD_FONT = "'Barlow Condensed', sans-serif";

/* Status pill — same semantic palette as Seasons.jsx so the visual
 * vocabulary is identical across the two surfaces. */
function StatusPill({ status }) {
  const map = {
    open: {
      label: 'Open',
      icon: LockOpenIcon,
      cls: 'bg-[#b5f03d] text-[#0d1f14] ring-1 ring-[#94cc28]',
    },
    closed: {
      label: 'Closed',
      icon: LockClosedIcon,
      cls: 'bg-zinc-100 text-zinc-600 ring-1 ring-zinc-200',
    },
    draft: {
      label: 'Draft',
      icon: null,
      cls: 'bg-zinc-50 text-zinc-500 ring-1 ring-zinc-200',
    },
    archived: {
      label: 'Archived',
      icon: ArchiveBoxIcon,
      cls: 'bg-white text-zinc-500 ring-1 ring-zinc-300',
    },
  };
  const item = map[status] ?? map.draft;
  const Icon = item.icon;
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.15em] shrink-0',
        item.cls
      )}
      style={{ fontFamily: HEAD_FONT }}
    >
      {Icon && <Icon className="h-3 w-3" />}
      {item.label}
    </span>
  );
}

/* Firestore Timestamps come back in a few shapes (live snapshot,
 * cached, plain 'YYYY-MM-DD' from edits) — shared convention keeps
 * these date-only fields on the coach's calendar day. */
const toDate = toSeasonDate;

function fmtRange(start, end) {
  const s = toDate(start);
  const e = toDate(end);
  if (!s || !e) return null;
  return `${format(s, 'MMM d')} – ${format(e, 'MMM d, yyyy')}`;
}

export default function TeamSeasonsList() {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const [seasons, setSeasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [regCounts, setRegCounts] = useState({});
  const [eventCounts, setEventCounts] = useState({});

  // Seasons live in a flat top-level `seasons` collection keyed by
  // teamId — same query Seasons.jsx and TeamScheduleRedirect both use.
  // No composite index required: where('teamId','==',…) only.
  useEffect(() => {
    if (!teamId) {
      setSeasons([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const q = query(
      collection(db, 'seasons'),
      where('teamId', '==', teamId)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setSeasons(list);
        setLoading(false);
      },
      (err) => {
        console.error('[TeamSeasonsList] seasons', err);
        setLoading(false);
      }
    );
    return unsub;
  }, [teamId]);

  // Registration counts per season — flat `registrations` collection,
  // grouped client-side. Cancelled rows excluded so the count matches
  // what coaches see on SeasonDetail.
  useEffect(() => {
    if (!teamId) return;
    const q = query(
      collection(db, 'registrations'),
      where('teamId', '==', teamId)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const counts = {};
        snap.forEach((d) => {
          const data = d.data();
          if (data.status === 'cancelled') return;
          const sid = data.seasonId;
          if (!sid) return;
          counts[sid] = (counts[sid] || 0) + 1;
        });
        setRegCounts(counts);
      },
      (err) => console.error('[TeamSeasonsList] regCounts', err)
    );
    return unsub;
  }, [teamId]);

  // Scheduled-event counts per season. Events are stored under
  // `events` (flat) with a seasonId field — same shape SeasonSchedule
  // reads. Cheap to count client-side; a team rarely has more than a
  // few hundred events across all seasons.
  useEffect(() => {
    if (!teamId) return;
    const q = query(
      collection(db, 'events'),
      where('teamId', '==', teamId)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const counts = {};
        snap.forEach((d) => {
          const data = d.data();
          const sid = data.seasonId;
          if (!sid) return;
          counts[sid] = (counts[sid] || 0) + 1;
        });
        setEventCounts(counts);
      },
      (err) => {
        // Non-fatal — events collection may not exist for every team.
        // We just hide the count rather than blowing up the page.
        console.warn('[TeamSeasonsList] eventCounts', err?.message || err);
      }
    );
    return unsub;
  }, [teamId]);

  // Sort: open first, then closed by date desc, then archived. Keeps
  // the active season at the top where coaches expect it.
  const sortedSeasons = useMemo(() => {
    const rank = { open: 0, closed: 1, draft: 1, archived: 2 };
    return [...seasons].sort((a, b) => {
      const ra = rank[a.status] ?? 1;
      const rb = rank[b.status] ?? 1;
      if (ra !== rb) return ra - rb;
      const ta = a.createdAt?.seconds ?? 0;
      const tb = b.createdAt?.seconds ?? 0;
      return tb - ta;
    });
  }, [seasons]);

  return (
    <div className="min-h-screen bg-[#f7faf5]">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Team sub-nav: keeps Overview / Roster / Seasons / Schedule
            / Chat tabs visible while drilled into team-scoped content. */}
        <TeamSubNav teamId={teamId} />

        {/* Header + Create CTA. Lime button matches the rest of the
            seasons surface (Seasons.jsx, SeasonDetail.jsx). */}
        <header className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <p
              className="text-xs font-semibold uppercase tracking-[0.18em] text-[#5a8a1f]"
              style={{ fontFamily: HEAD_FONT }}
            >
              Seasons
            </p>
            <h1
              className="mt-1 text-2xl sm:text-3xl font-bold uppercase tracking-tight text-[#0d1f14]"
              style={{ fontFamily: HEAD_FONT }}
            >
              All seasons
            </h1>
            <p className="mt-1 text-sm text-zinc-500">
              Every season you've ever run for this team. Drill in to manage
              registrations, schedule, attendance, and more.
            </p>
          </div>
          <button
            onClick={() => navigate('/seasons/create')}
            className="inline-flex items-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-4 py-2.5 text-sm font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all active:scale-[0.97]"
            style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
          >
            <PlusIcon className="h-4 w-4" />
            Create Season
          </button>
        </header>

        {loading ? (
          <div className="space-y-4">
            {[1, 2].map((n) => (
              <div
                key={n}
                className="h-32 rounded-2xl bg-zinc-100 animate-pulse"
              />
            ))}
          </div>
        ) : sortedSeasons.length === 0 ? (
          <div className="rounded-2xl border border-zinc-200 bg-white p-10 sm:p-12 text-center shadow-[0_1px_2px_0_rgba(13,31,20,0.04)]">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40">
              <CalendarDaysIcon className="h-7 w-7 text-[#5a8a1f]" />
            </div>
            <h2
              className="text-xl font-bold uppercase tracking-tight text-[#0d1f14]"
              style={{ fontFamily: HEAD_FONT }}
            >
              No seasons yet
            </h2>
            <p className="mt-1.5 text-sm text-zinc-500 max-w-md mx-auto">
              Create your first season to start collecting registrations and
              scheduling games.
            </p>
            <button
              onClick={() => navigate('/seasons/create')}
              className="mt-6 inline-flex items-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-4 py-2.5 text-sm font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all active:scale-[0.97]"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
            >
              <PlusIcon className="h-4 w-4" />
              Create First Season
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {sortedSeasons.map((s) => {
              const regWindow = fmtRange(s.registrationOpen, s.registrationClose);
              const seasonWindow = fmtRange(s.seasonStart, s.seasonEnd);
              const regCount = regCounts[s.id] ?? 0;
              const eventCount = eventCounts[s.id] ?? 0;
              const maxPlayers = s.maxPlayers ?? null;

              return (
                <Link
                  key={s.id}
                  to={`/seasons/${s.id}`}
                  className="group block rounded-2xl border border-zinc-200 bg-white shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] hover:border-[#b5f03d]/60 hover:bg-[#b5f03d]/5 transition-all overflow-hidden"
                >
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <h3
                          className="text-lg font-bold uppercase tracking-tight text-[#0d1f14] truncate group-hover:text-[#3d6010] transition-colors"
                          style={{ fontFamily: HEAD_FONT }}
                        >
                          {s.name || 'Untitled season'}
                        </h3>
                        <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-500">
                          {regWindow && (
                            <span>
                              <span className="font-semibold text-zinc-600">Reg:</span>{' '}
                              {regWindow}
                            </span>
                          )}
                          {seasonWindow && (
                            <span>
                              <span className="font-semibold text-zinc-600">
                                Season:
                              </span>{' '}
                              {seasonWindow}
                            </span>
                          )}
                        </div>
                      </div>
                      <StatusPill status={s.status} />
                    </div>

                    <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
                      <span className="flex items-center gap-1.5 text-zinc-600">
                        <ClipboardDocumentListIcon className="h-4 w-4 text-zinc-400" />
                        <span className="font-medium text-[#0d1f14]">
                          {regCount}
                          {maxPlayers ? ` / ${maxPlayers}` : ''}
                        </span>
                        <span className="text-zinc-500">
                          {regCount === 1 ? 'registration' : 'registrations'}
                        </span>
                      </span>
                      <span className="flex items-center gap-1.5 text-zinc-600">
                        <CalendarDaysIcon className="h-4 w-4 text-zinc-400" />
                        <span className="font-medium text-[#0d1f14]">
                          {eventCount}
                        </span>
                        <span className="text-zinc-500">
                          {eventCount === 1 ? 'event' : 'events'} scheduled
                        </span>
                      </span>
                    </div>
                  </div>

                  {/* "View" CTA strip — clicking the whole card navigates,
                      but the explicit affordance gives the visual hierarchy
                      coaches expect on a list page. */}
                  <div className="flex items-center justify-between gap-2 border-t border-zinc-200 px-5 py-3 bg-[#f7faf5] group-hover:bg-[#b5f03d]/10 transition-colors">
                    <span
                      className="text-[11px] font-bold uppercase tracking-[1.2px] text-zinc-500"
                      style={{ fontFamily: HEAD_FONT }}
                    >
                      Manage registrations · schedule · attendance · finances
                    </span>
                    <span
                      className="inline-flex items-center gap-1 text-xs font-bold uppercase text-[#5a8a1f] group-hover:text-[#3d6010]"
                      style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                    >
                      View
                      <ArrowRightIcon className="h-3.5 w-3.5" />
                    </span>
                  </div>
                </Link>
              );
            })}

            {/* Helpful footnote — reinforces that Schedule lives inside
                a season, not next to it. Reduces the "where do I go?"
                question that prompted this restructure. */}
            <p className="pt-3 text-center text-xs text-zinc-400">
              <UserGroupIcon className="inline h-3 w-3 mr-1 align-text-bottom" />
              Each season owns its own schedule, roster, and registrations.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
